import Modal from 'bootstrap/js/dist/modal';
import { getModelInfo, populateBrands, populateModel } from './modelLookup';

const prodUrlMap = ((<any>window).prodUrlMap || {}) as Record<string, string>;

const templateHtml = `
  <div class="modal fade" data-bs-keyboard="false" tabindex="-1" aria-labelledby="staticBackdropLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Does it fit my radio?</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body text-start">
          <div class="mb-3 row align-items-center">
            <label for="brand-select" class="col-sm-3 col-form-label">Radio Brand</label>
            <div class="col-sm-9">
              <select class="form-select" id="brand-select">
                <option value="" selected>Select your radio brand</option>
              </select>
            </div>
          </div>
          <div class="mb-3 row align-items-center">
            <label for="model-select" class="col-sm-3 col-form-label">Radio Model</label>
            <div class="col-sm-9">
              <select class="form-select" id="model-select">
                <option value="" selected>Select brand first</option>
              </select>
            </div>
          </div>
          <div id="result" class="alert d-flex d-none align-items-center"></div>
          <div id="notes" class="mt-3">
            <h5>Notes</h5>
            <ul>
              <li>This check is for connector fit only!</li>
              <li>Signal Sticks™ are intended for handheld use; other uses may require adding a counterpoise.</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  </div>
`;

function reverseConnector(type: string) {
  // SMA-M -> SMA-F, SMA-F -> SMA-M, BNC -> BNC
  if (type.startsWith('SMA-')) {
    return type === 'SMA-M' ? 'SMA-F' : 'SMA-M';
  }
  return type;
}

function getAdapterInfo(antennaConnector: string, radioConnector: string): false | { message: string, url?: string } {
  if (antennaConnector === radioConnector) {
    return false;
  } else if (antennaConnector === 'SMA-M' && radioConnector === 'SMA-F') {
    return {
      message: `SMA-F to SMA-F barrell adapter`,
      url: prodUrlMap['SMA-F-to-SMA-F']
    };
  } else if (antennaConnector === 'SMA-F' && radioConnector === 'SMA-M') {
    return {
      message: `SMA-M to SMA-M barrell adapter`,
      url: prodUrlMap['SMA-M-to-SMA-M']
    };
  } else if (antennaConnector === 'BNC' && radioConnector.startsWith('SMA-')) {
    return {
      message: `${radioConnector} to BNC-F adapter`,
      url: prodUrlMap[`${radioConnector}-to-BNC-F`]
    };
  } else if (radioConnector === "BNC" && antennaConnector.startsWith('SMA-')) {
    const needed = reverseConnector(antennaConnector);
    return {
      message: `BNC-M to ${needed} adapter`,
      url: prodUrlMap[`BNC-M-to-${needed}`]
    }
  }

  throw new Error("Invalid connector type");
}

export function display(antennaConnector: string): Promise<void> {
  const template = document.createElement('template');
  template.innerHTML = templateHtml.trim();
  const modalElement = template.content.firstChild as HTMLDivElement;
  const modal = new Modal(modalElement);
  
  setupListeners(modalElement, modal, antennaConnector);
  populateForm(modalElement);
  
  modal.show();
  return new Promise((resolve) => {
    modalElement.addEventListener('hidden.bs.modal', () => {
      modalElement.remove();
      resolve();
    });
  });
}

function setupListeners(modalElement: HTMLDivElement, modal: Modal, antennaConnector: string) {
  const brandSelect = modalElement.querySelector('#brand-select') as HTMLSelectElement;
  const modelSelect = modalElement.querySelector('#model-select') as HTMLSelectElement;
  const resultDiv = modalElement.querySelector('#result') as HTMLDivElement;

  brandSelect.addEventListener('change', () => {
    populateModel($(brandSelect), $(modelSelect));
    resultDiv.classList.add('d-none');
  });

  modelSelect.addEventListener('change', () => {
    const modelInfo = getModelInfo(brandSelect.value, modelSelect.value);
    if (modelInfo) {
      window.plausible?.('compatibility-check', { props: { brand: brandSelect.value, model: modelSelect.value } });
      const adapter = getAdapterInfo(antennaConnector, modelInfo.connector);
      const needsSpacer = modelInfo.spacer === "1";
      
      let message = '';
      let className = '';

      if (!adapter) {
        message = `<i class="fa fa-check-circle fa-xl me-4"></i>
        <div>
          This antenna fits your radio directly${needsSpacer ? ', but we suggest adding a spacer' : '; no spacer needed'}.
        </div>`;
        className = 'alert-success';
      } else {
        message = `<i class="fa fa-exclamation-circle fa-xl me-4"></i>
          <div>
            You can use this antenna with a <a href="${adapter.url}">${adapter.message}</a>${needsSpacer ? ' and a spacer' : ''}.
            <br><br>If you want to fit your radio without any adapter you need a <a href="${prodUrlMap[modelInfo.connector]}">${modelInfo.connector} antenna</a>.
          </div>`;
        className = 'alert-warning';
      }

      resultDiv.className = `alert ${className} d-flex align-items-start text-start`;
      resultDiv.innerHTML = message;
    }
  });
}

function populateForm(modalElement: HTMLDivElement) {
  const brandSelect = modalElement.querySelector('#brand-select') as HTMLSelectElement;
  populateBrands($(brandSelect));
}